(() => {
  'use strict';

  const configElement = document.getElementById('theme-config');
  if (!configElement) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch { return; }
  const { routes, strings } = config;
  const productStates = new WeakMap();
  const carousels = new WeakMap();
  let cartBusy = false;
  let cartUncertain = false;
  let toastTimer;

  const findAll = (scope, selector) => [
    ...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)
  ];

  function showToast(message) {
    const toast = document.getElementById('toast');
    if (!toast) return;
    clearTimeout(toastTimer);
    toast.textContent = message;
    toast.classList.add('show');
    toastTimer = setTimeout(() => toast.classList.remove('show'), 5000);
  }

  function showError(scope, message, selector = '[data-form-error]') {
    const error = scope.querySelector(selector);
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  // Never retry a mutation automatically: a lost response may still mean the
  // operation succeeded. Read Shopify's state instead of inventing local totals.
  async function request(url, options = {}, asText = false) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {
        ...options, signal: controller.signal, credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: asText ? 'text/html' : 'application/json', ...options.headers }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const error = new Error(typeof data.description === 'string' ? data.description : strings.error);
        error.status = response.status;
        throw error;
      }
      return asText ? await response.text() : await response.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  function lockControls(scopes) {
    const saved = [];
    scopes.forEach(scope => {
      scope.setAttribute('aria-busy', 'true');
      scope.querySelectorAll('button, input, select, textarea').forEach(control => {
        saved.push([control, control.disabled]);
        control.disabled = true;
      });
    });
    return () => {
      saved.forEach(([control, disabled]) => { control.disabled = disabled; });
      scopes.forEach(scope => scope.removeAttribute('aria-busy'));
    };
  }

  function updateCount(count) {
    if (!Number.isFinite(Number(count))) return;
    document.querySelectorAll('[data-cart-count]').forEach(badge => { badge.textContent = count; });
  }

  async function refreshCount() {
    const cart = await request(`${routes.cart}.js`);
    updateCount(cart.item_count);
    return cart;
  }

  function replaceCart(root, html, focus = null) {
    if (typeof html !== 'string') throw new Error(strings.reload);
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('[data-cart-root]');
    if (!replacement || replacement.dataset.sectionId !== root.dataset.sectionId) throw new Error(strings.reload);
    root.replaceWith(replacement);
    initialize(replacement);
    updateCount(replacement.dataset.itemCount);
    if (focus) {
      const rows = [...replacement.querySelectorAll('[data-key]')];
      const row = rows.find(item => item.dataset.key === focus.key) || rows[Math.min(focus.index, rows.length - 1)];
      const target = row?.querySelector(focus.selector) || replacement.querySelector('h1');
      target?.focus({ preventScroll: true });
    }
    return replacement;
  }

  function requireCartReload(root, message) {
    cartUncertain = true;
    lockControls([root]);
    showError(root, message, '[data-cart-error]');
    const link = document.createElement('a');
    link.href = routes.cart;
    link.className = 'btn btn-secondary';
    link.textContent = strings.reload;
    root.querySelector('[data-cart-error]')?.append(' ', link);
  }

  async function reloadCartSection(root, focus) {
    const url = new URL(routes.cart, location.origin);
    url.searchParams.set('section_id', root.dataset.sectionId);
    return replaceCart(root, await request(url.toString(), {}, true), focus);
  }

  async function changeCart(root, row, quantity, trigger) {
    if (cartBusy || cartUncertain) return;
    cartBusy = true;
    const input = row.querySelector('[data-cart-quantity]');
    const key = row.dataset.key;
    const focus = {
      key, index: [...root.querySelectorAll('[data-key]')].indexOf(row),
      selector: trigger?.matches('[data-qty-delta]') ? `[data-qty-delta="${trigger.dataset.qtyDelta}"]` : '[data-cart-quantity]'
    };
    const unlock = lockControls([root]);
    showError(root, '', '[data-cart-error]');
    let synchronized = true;
    try {
      const cart = await request(routes.change, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [root.dataset.sectionId], sections_url: routes.cart })
      });
      try {
        root = replaceCart(root, cart.sections?.[root.dataset.sectionId], focus);
      } catch {
        root = await reloadCartSection(root, focus);
      }
      const updatedRow = [...root.querySelectorAll('[data-key]')].find(item => item.dataset.key === key);
      const accepted = Number(updatedRow?.querySelector('[data-cart-quantity]')?.value || 0);
      showToast(accepted !== quantity && updatedRow ? strings.quantityAdjusted : strings.cartUpdated);
    } catch (error) {
      input.value = input.dataset.serverQuantity;
      // Reconcile even after an error: the POST may have been accepted before
      // the connection was interrupted. Never leave checkout on stale quantities.
      try { root = await reloadCartSection(root, focus); } catch { synchronized = false; }
      showError(root, error.status ? error.message : strings.error, '[data-cart-error]');
      if (!synchronized) requireCartReload(root, error.status ? error.message : strings.error);
    } finally {
      if (synchronized) unlock();
      cartBusy = false;
    }
  }

  async function addToCart(form) {
    if (cartBusy || cartUncertain || !form.reportValidity()) return;
    const body = new FormData(form);
    let cartRoot = document.querySelector('[data-cart-root]');
    if (cartRoot) {
      body.set('sections', cartRoot.dataset.sectionId);
      body.set('sections_url', routes.cart);
    }
    cartBusy = true;
    const unlock = lockControls([...document.querySelectorAll('[data-product-form]')]);
    const unlockCart = cartRoot ? lockControls([cartRoot]) : null;
    showError(form, '');
    try {
      const added = await request(routes.add, { method: 'POST', body });
      let message = strings.added;
      try {
        if (cartRoot) {
          try { cartRoot = replaceCart(cartRoot, added.sections?.[cartRoot.dataset.sectionId]); }
          catch { cartRoot = await reloadCartSection(cartRoot); }
        } else {
          await refreshCount();
        }
      } catch {
        message += ` ${strings.reload}`;
        if (cartRoot) requireCartReload(cartRoot, strings.error);
      }
      showToast(message);
    } catch (error) {
      showError(form, error.status ? error.message : strings.error);
      // Read-only reconciliation. In particular, do NOT form.submit() here.
      try {
        if (cartRoot) await reloadCartSection(cartRoot);
        else await refreshCount();
      } catch {
        if (cartRoot) requireCartReload(cartRoot, strings.error);
      }
    } finally {
      unlock();
      if (!cartUncertain) unlockCart?.();
      cartBusy = false;
    }
  }

  function plainMoney(value) {
    // Liquid's shared price snippet has already applied the USD display format.
    // Decode entities in that trusted, stripped string before updating the DOM.
    const parsed = new DOMParser().parseFromString(String(value || ''), 'text/html');
    return parsed.body.textContent;
  }

  function setImage(root, image) {
    const main = root.querySelector('.main-product-image');
    if (!main || !image) return;
    main.removeAttribute('srcset');
    main.removeAttribute('sizes');
    main.src = image.src;
    main.alt = image.alt || '';
    if (image.width && image.height) { main.width = image.width; main.height = image.height; }
    root.querySelectorAll('[data-thumbnail]').forEach(thumb => {
      const active = thumb.dataset.imageId === String(image.id);
      thumb.classList.toggle('active', active);
      thumb.setAttribute('aria-current', String(active));
    });
  }

  function variantHistory(root, variant) {
    if (!location.pathname.includes('/products/')) return;
    const url = new URL(location.href);
    url.searchParams.delete('variant');
    url.searchParams.delete('option_values');
    if (variant) url.searchParams.set('variant', variant.id);
    else {
      const ids = [...root.querySelectorAll('[data-option-value].active')].map(option => option.dataset.optionValueId);
      if (ids.length && ids.every(Boolean)) url.searchParams.set('option_values', ids.join(','));
    }
    history.replaceState({}, '', url);
  }

  function selectVariant(root, variant, { history = false, image = true, resetPlans = true } = {}) {
    const state = productStates.get(root);
    state.variant = variant;
    const select = root.querySelector('[data-variant-select]');
    if (variant && ![...select.options].some(option => option.value === variant.id)) {
      select.add(new Option(variant.options.join(' / '), variant.id));
    }
    select.value = variant?.id || '';
    select.disabled = !variant;
    const planSelect = root.querySelector('[data-selling-plan]');
    if (planSelect && resetPlans) {
      const previous = planSelect.value;
      planSelect.replaceChildren();
      if (!state.requiresSellingPlan) planSelect.add(new Option(strings.oneTime, ''));
      (variant?.plans || []).forEach(plan => planSelect.add(new Option(plan.name, plan.id)));
      if ([...planSelect.options].some(option => option.value === previous)) planSelect.value = previous;
    }
    const plan = variant?.plans?.find(item => item.id === planSelect?.value);
    const available = variant?.available === true && (!state.requiresSellingPlan || Boolean(plan));
    const status = !variant ? strings.unavailable : !variant.available ? strings.soldOut : state.requiresSellingPlan && !plan ? strings.subscriptionRequired : strings.inStock;
    const price = root.querySelector('[data-product-price]');
    price.textContent = variant ? plainMoney((plan || variant).price) : '';
    const compare = root.querySelector('[data-compare-price]');
    compare.textContent = plainMoney((plan || variant)?.comparePrice);
    compare.hidden = !(plan || variant)?.comparePrice;
    const badge = root.querySelector('[data-stock-badge]');
    badge.textContent = status;
    badge.classList.toggle('in-stock', available);
    badge.classList.toggle('out-of-stock', !available);
    root.querySelectorAll('[data-add-button]').forEach(button => {
      button.disabled = !available;
      button.textContent = available ? strings.addToCart : status;
    });
    root.querySelectorAll('[data-direct-checkout]').forEach(container => {
      container.hidden = !available;
    });
    if (variant) {
      const input = root.querySelector('[name="quantity"]');
      const rule = variant.quantity;
      input.min = rule.min; input.step = rule.increment;
      if (rule.max != null) input.max = rule.max; else input.removeAttribute('max');
      const value = Number(input.value) || rule.min;
      input.value = Math.max(rule.min, Math.min(rule.max ?? Infinity, rule.min + Math.floor((value - rule.min) / rule.increment) * rule.increment));
      const ruleText = root.querySelector('[data-quantity-rules]');
      ruleText.textContent = rule.message;
      ruleText.hidden = rule.min === 1 && rule.increment === 1 && rule.max == null;
    }
    if (image && variant) setImage(root, variant.image || state.defaultImage);
    if (history) variantHistory(root, variant);
    showError(root, '');
  }

  function initializeProduct(root) {
    if (productStates.has(root)) return;
    let state;
    try { state = JSON.parse(root.querySelector('[data-product-data]').textContent); } catch { return; }
    productStates.set(root, state);
    const options = root.querySelector('[data-variant-options]');
    if (options) {
      options.hidden = false;
      root.querySelector('[data-native-variants]').hidden = true;
    }
    selectVariant(root, state.variants.find(variant => variant.id === state.selectedId), { image: false });
    if (root.querySelector('model-viewer') && typeof window.Shopify?.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: error => {
        if (error || !root.isConnected || typeof window.Shopify.ModelViewerUI !== 'function') return;
        root.querySelectorAll('model-viewer').forEach(model => new window.Shopify.ModelViewerUI(model));
      } }]);
    }
  }

  async function selectRemoteVariant(root, trigger) {
    if (root.dataset.loading === 'true') return;
    root.dataset.loading = 'true';
    const unlock = lockControls([root.querySelector('[data-product-form]')]);
    const url = new URL(root.dataset.productUrl, location.origin);
    url.searchParams.set('section_id', root.dataset.sectionId);
    url.searchParams.set('option_values', [...root.querySelectorAll('[data-option-value].active')].map(option => option.dataset.optionValueId).join(','));
    const quantity = root.querySelector('[name="quantity"]').value;
    const selectedId = trigger.dataset.optionValueId;
    try {
      const html = await request(url.toString(), {}, true);
      const replacement = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-product-root]');
      if (!replacement || replacement.dataset.sectionId !== root.dataset.sectionId) throw new Error(strings.error);
      replacement.querySelector('[name="quantity"]').value = quantity;
      root.replaceWith(replacement);
      initialize(replacement);
      const state = productStates.get(replacement);
      variantHistory(replacement, state.variant);
      [...replacement.querySelectorAll('[data-option-value]')].find(option => option.dataset.optionValueId === selectedId)?.focus({ preventScroll: true });
    } catch {
      // Do not allow the old variant ID to be bought under a new option label.
      unlock();
      selectVariant(root, null, { image: false });
      showError(root, strings.error);
    } finally {
      root.dataset.loading = 'false';
    }
  }

  function initializeCarousel(root) {
    if (carousels.has(root)) return;
    const slides = [...root.querySelectorAll('[data-slide]')];
    if (!slides.length) return;
    const dots = [...root.querySelectorAll('[data-slide-index]')];
    const controller = new AbortController();
    const { signal } = controller;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let index = 0, timer;
    let paused = reducedMotion.matches || Boolean(window.Shopify?.designMode);
    let hovered = false, focused = false;
    const pauseButton = root.querySelector('[data-carousel-pause]');
    const updatePauseButton = () => {
      if (!pauseButton) return;
      pauseButton.hidden = reducedMotion.matches || Boolean(window.Shopify?.designMode);
      pauseButton.textContent = paused ? '▶' : 'Ⅱ';
      pauseButton.setAttribute('aria-label', paused ? pauseButton.dataset.playLabel : pauseButton.dataset.pauseLabel);
    };
    const schedule = () => {
      clearTimeout(timer);
      if (root.dataset.autoplay === 'true' && slides.length > 1 && !paused && !hovered && !focused && !document.hidden && !reducedMotion.matches && !window.Shopify?.designMode) {
        timer = setTimeout(() => show(index + 1), Math.max(3000, Number(root.dataset.interval) || 3000));
      }
    };
    const show = next => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.hidden = i !== index;
        slide.classList.toggle('active', i === index);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
        dot.setAttribute('aria-current', String(i === index));
      });
      schedule();
    };
    root.querySelector('[data-carousel-controls]')?.removeAttribute('hidden');
    root.addEventListener('click', event => {
      const step = event.target.closest('[data-carousel-step]');
      const dot = event.target.closest('[data-slide-index]');
      if (step) show(index + Number(step.dataset.carouselStep));
      if (dot) show(Number(dot.dataset.slideIndex));
      if (event.target.closest('[data-carousel-pause]')) { paused = !paused; updatePauseButton(); schedule(); }
    }, { signal });
    root.addEventListener('mouseenter', () => { hovered = true; schedule(); }, { signal });
    root.addEventListener('mouseleave', () => { hovered = false; schedule(); }, { signal });
    root.addEventListener('focusin', () => { focused = true; schedule(); }, { signal });
    root.addEventListener('focusout', event => { focused = root.contains(event.relatedTarget); schedule(); }, { signal });
    document.addEventListener('visibilitychange', schedule, { signal });
    reducedMotion.addEventListener('change', () => { if (reducedMotion.matches) paused = true; updatePauseButton(); schedule(); }, { signal });
    root.addEventListener('shopify:block:select', event => {
      const blockIndex = slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
      if (blockIndex >= 0) { paused = true; updatePauseButton(); show(blockIndex); }
    }, { signal });
    carousels.set(root, () => { clearTimeout(timer); controller.abort(); carousels.delete(root); });
    updatePauseButton(); show(0);
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-qty-delta]').forEach(button => { button.hidden = false; });
    findAll(scope, '[data-product-root]').forEach(initializeProduct);
    findAll(scope, '[data-carousel]').forEach(initializeCarousel);
  }

  document.addEventListener('click', event => {
    const target = event.target;
    const qtyButton = target.closest('[data-qty-delta]');
    if (qtyButton) {
      const input = qtyButton.closest('.qty-control').querySelector('input');
      if (input.disabled) return;
      const delta = Number(qtyButton.dataset.qtyDelta);
      const min = Number(input.min) || 0;
      const step = Number(input.step) || 1;
      const current = Number(input.value) || min;
      let next = Math.max(min, Math.min(input.max === '' ? Infinity : Number(input.max), current + delta * step));
      if (input.hasAttribute('data-cart-quantity')) {
        if (cartBusy || cartUncertain) return;
        const ruleMin = Number(input.dataset.quantityMin) || 1;
        if (delta < 0 && next < ruleMin) next = 0;
        if (delta > 0 && current === 0) next = ruleMin;
        input.value = next;
        if (next !== current) changeCart(input.closest('[data-cart-root]'), input.closest('[data-key]'), next, qtyButton);
      } else {
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    const remove = target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      changeCart(remove.closest('[data-cart-root]'), remove.closest('[data-key]'), 0, remove);
    }
    const option = target.closest('[data-option-value]');
    if (option && !option.disabled) {
      const root = option.closest('[data-product-root]');
      const state = productStates.get(root);
      if (!state) return;
      const group = option.closest('[data-option-index]');
      group.querySelectorAll('[data-option-value]').forEach(button => {
        button.classList.toggle('active', button === option);
        button.setAttribute('aria-pressed', String(button === option));
      });
      group.querySelector('.selected-value-text').textContent = option.dataset.optionValue;
      if (state.hasMoreVariants) { selectRemoteVariant(root, option); return; }
      const selected = [...root.querySelectorAll('[data-option-index]')].map(item => item.querySelector('.active')?.dataset.optionValue);
      const variant = state.variants.find(item => item.options.length === selected.length && item.options.every((value, i) => value === selected[i]));
      selectVariant(root, variant, { history: true });
    }
    const thumb = target.closest('[data-thumbnail]');
    if (thumb) {
      event.preventDefault();
      setImage(thumb.closest('[data-product-root]'), { id: thumb.dataset.imageId, src: thumb.dataset.src, alt: thumb.dataset.alt, width: Number(thumb.dataset.width), height: Number(thumb.dataset.height) });
    }
    document.querySelectorAll('[data-language-selector][open]').forEach(details => {
      if (!details.contains(target)) details.open = false;
    });
  });

  document.addEventListener('change', event => {
    const input = event.target;
    if (input.matches('[data-cart-quantity]')) {
      if (cartBusy || cartUncertain) return;
      const quantity = Number(input.value);
      const min = Number(input.dataset.quantityMin) || 1;
      if (!input.reportValidity() || !Number.isInteger(quantity) || (quantity !== 0 && quantity < min)) {
        input.value = input.dataset.serverQuantity;
        return;
      }
      if (quantity !== Number(input.dataset.serverQuantity)) changeCart(input.closest('[data-cart-root]'), input.closest('[data-key]'), quantity, input);
    }
    const root = input.closest('[data-product-root]');
    const state = root && productStates.get(root);
    if (state && input.matches('[data-selling-plan]')) selectVariant(root, state.variant, { resetPlans: false, image: false });
  });

  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches('.cart-form') && (cartBusy || cartUncertain)) { event.preventDefault(); return; }
    if (!form.matches('[data-product-form]')) return;
    // Accelerated checkout belongs to Shopify, not to the AJAX add-to-cart flow.
    if (event.submitter?.closest('[data-direct-checkout]')) return;
    event.preventDefault();
    addToCart(form);
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const details = event.target.closest('details[open]');
    if (details) { details.open = false; details.querySelector('summary')?.focus(); }
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    findAll(event.target, '[data-carousel]').forEach(root => carousels.get(root)?.());
  });
  // Back/forward cache can otherwise show a badge from before a cart mutation.
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    if (document.querySelector('[data-cart-root]')) location.reload();
    else refreshCount().catch(() => {});
  });

  // Shopify inserts/re-renders this control asynchronously. Change its label
  // only; leave the native checkout action and the other payment buttons intact.
  function labelDirectCheckout() {
    document.querySelectorAll('[data-direct-checkout] .shopify-payment-button__more-options').forEach(button => {
      if (button.textContent !== strings.buyNow) button.textContent = strings.buyNow;
      if (button.hasAttribute('aria-label') && button.getAttribute('aria-label') !== strings.buyNow) {
        button.setAttribute('aria-label', strings.buyNow);
      }
    });
  }
  const paymentLabelObserver = new MutationObserver(labelDirectCheckout);
  paymentLabelObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
  labelDirectCheckout();

  initialize();
})();
